import { components } from '../../../generated/api/schema';
import { AttributeAbbreviation } from './attribute-abbreviation';

export type TripAddressType = 'destination' | 'pickup' | 'via';

export type Address = components['schemas']['OrderAddress'];

export type TripStatus = Required<components['schemas']['Order']>['tripStatus'];

interface Attribute {
  abbreviation: string;
  amount: number;
  attributeID: AttributeAbbreviation;
  description: string;
}

interface Permission {
  allowed: boolean;
  group: string;
  name: string;
}

export type TripStatusState =
  | 'deleted'
  | 'dispatching'
  | 'booked'
  | 'customer'
  | 'booked'
  | 'deleted'
  | 'cancelled'
  | 'blocked'
  | 'noshow'
  | 'arriving'
  | 'concluded'
  | 'completed';

interface Status {
  carNo: number;
  driverNo: number;
  operator: string;
  price: number;
  provider?: string;
  state: TripStatusState;
  timeStamp: Date;
  tripAssigned: Date;
  tripEnded?: Date;
  tripStarted?: Date;
}

export interface Trip {
  account: number;
  addresses: Address[];
  agreementID: number;
  attributes: Attribute[];
  bookRefNo: number;
  bookSeqNo: number;
  combinedStatus: string;
  comment: string;
  companyNo: number;
  coordinable: boolean;
  coordinatedBookRefnr: number;
  createdDateTime: string;
  customerRefNo: string;
  dropOffTimeDateTime: string;
  email: string;
  firstName: string;
  fixedCar: number;
  fixedDriver: number;
  fixedPrice: number;
  lastName: string;
  manual: boolean;
  metaData: { isEditable: boolean; isReopenable: boolean };
  operator: string;
  permissions: Permission[];
  personID: number;
  phone: string;
  pickUpDateTime: string;
  productID: number;
  projectID: string;
  scheduleID: number;
  status: Status;
  urgent: boolean;
}

export interface GetTripsResponse {
  count: number;
  items: GetTripResponse[];
}

type SortOrder = 'ASC' | 'DESC';

export interface GetTripsPayload {
  personId?: number;
  name?: string;
  searchItem?: string;
  address?: string;
  fromDate?: string;
  toDate?: string;
  grouping?: boolean;
  itemsPerPage?: number;
  index?: number;
  sortField?: string | null;
  sortOrder?: SortOrder;
  agreementIds?: string;
}

export type GpsPosition = {
  x: number;
  y: number;
  id: number;
  timeStamp: string;
};

export enum TripPersonType {
  PickedUp = 0,
  DroppedOff = 1,
  NoShow = 2,
}

export type TripPerson = {
  tripRefId: number;
  personIndex: number;
  type: TripPersonType;
  timeStamp: string;
};

export type GetTripResponse = components['schemas']['TripDto'];
export type TripDetailsDto = components['schemas']['TripDetailsDto'];

/**
 * The GET /v2/trips/{id} response type with fields corrected to nullable,
 * since the generated schema marks them as required but BE can return null.
 */
export type GetTripDetailsResponse = Omit<
  TripDetailsDto,
  'status' | 'companyNo' | 'actualPickUpDateTime' | 'actualDropOffDateTime'
> & {
  status: components['schemas']['OrderState'] | null;
  companyNo: number | null;
  externalTripId?: number;
  actualPickUpDateTime?: string | null;
  actualDropOffDateTime?: string | null;
};
export type UpdateTripRequest = components['schemas']['UpdateTripRequest'];

export type CancelTripData = {
  bookRefNo: number;
  userID?: number;
  commentOperator?: string;
  cancelComment?: string;
};

export type RebookTripData = Omit<Partial<GetTripResponse>, 'status'>;

export type ApiBaseResponse = {
  message: string;
  success: boolean;
};

export type SendMessageData = {
  pickUpDate: Date;
  bookingrefno: string;
  /// Subject is defined in the api documentation but is hard coded for now.
  // subject: string;
  fullName: string;
  message: string;
  companyNo: number;
};
